import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { UpgradeLockCard } from '../components/UpgradeLock';
import { useConfirm } from './ConfirmDialog';
import { PriorityPill } from './PriorityIcon';
import { getAvatarStyle, getInitials } from '../lib/avatarColor';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Plus, CaretDown, CaretRight, Play, CheckCircle, Trash, PencilSimple, DotsThree, Lightning, Target, X, ChartLine,
} from '@phosphor-icons/react';
import { WorkTypeIcon } from './KanbanBoard';

// Maps raw task status to a Title-Case label and a tone class for the badge.
// Default: todo / in_progress / review / done. Custom statuses fall through
// and resolve via project.custom_statuses[].label, with a final fallback that
// humanizes the underscore-separated key (e.g. "in_review" → "In Review").
const STATUS_TONE = {
  todo: 'bg-zinc-100 text-zinc-700 border-zinc-200',
  in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
  review: 'bg-amber-50 text-amber-700 border-amber-200',
  done: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};
const STATUS_LABEL = {
  todo: 'To Do',
  in_progress: 'In Progress',
  review: 'Review',
  done: 'Done',
};
function statusLabel(status, customStatuses = []) {
  if (STATUS_LABEL[status]) return STATUS_LABEL[status];
  const cs = (customStatuses || []).find(s => s.id === status);
  if (cs?.label) return cs.label;
  return String(status || '')
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map(w => w[0].toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}
function statusTone(status) {
  return STATUS_TONE[status] || 'bg-zinc-100 text-zinc-700 border-zinc-200';
}


// ─── Sprint header ────────────────────────────────────────────
function SprintHeader({ sprint, onStart, onComplete, onEdit, onDelete, canEdit, collapsed, onToggle, onCreateTask }) {
  const statusColor = {
    planning: 'bg-zinc-100 text-zinc-600 border-zinc-200',
    active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    completed: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  }[sprint.status];
  const total = sprint.story_points_total || 0;
  const done = sprint.story_points_completed || 0;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—';
  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-zinc-50 border-b border-zinc-200 sticky top-0 z-10" data-testid={`sprint-header-${sprint.sprint_id}`}>
      <button onClick={onToggle} className="text-zinc-500 hover:text-zinc-900 transition-colors flex-shrink-0">
        {collapsed ? <CaretRight size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-['Outfit'] text-[14px] font-bold text-zinc-900 truncate">{sprint.name}</span>
          <Badge className={`text-[9px] uppercase tracking-wider font-semibold border ${statusColor}`}>{sprint.status}</Badge>
          <span className="text-[11px] text-zinc-400">{formatDate(sprint.start_date)} → {formatDate(sprint.end_date)}</span>
        </div>
        {sprint.goal && <p className="text-[11px] text-zinc-500 mt-0.5 truncate">{sprint.goal}</p>}
      </div>
      <div className="flex items-center gap-3 text-[11px] text-zinc-600 flex-shrink-0">
        <span data-testid={`sprint-task-count-${sprint.sprint_id}`}>{sprint.completed_task_count || 0}/{sprint.task_count || 0} tasks</span>
        {total > 0 && (
          <span className="flex items-center gap-1.5" data-testid={`sprint-sp-${sprint.sprint_id}`}>
            <span className="font-semibold">{done}/{total}</span> SP
            <div className="w-16 h-1.5 bg-zinc-200 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
            </div>
          </span>
        )}
      </div>
      {canEdit && (
        <div className="flex items-center gap-1 flex-shrink-0">
          {sprint.status === 'planning' && (
            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={onStart} data-testid={`start-sprint-${sprint.sprint_id}`}>
              <Play size={11} className="mr-1" weight="fill" /> Start
            </Button>
          )}
          {sprint.status === 'active' && (
            <Button size="sm" className="h-7 text-[11px] bg-indigo-600 hover:bg-indigo-700 text-white" onClick={onComplete} data-testid={`complete-sprint-${sprint.sprint_id}`}>
              <CheckCircle size={11} className="mr-1" weight="fill" /> Complete
            </Button>
          )}
          {sprint.status !== 'completed' && (
            <>
              <button onClick={onCreateTask} className="h-7 w-7 flex items-center justify-center rounded hover:bg-zinc-100 text-zinc-500" title="Add task" data-testid={`add-task-to-sprint-${sprint.sprint_id}`}>
                <Plus size={13} />
              </button>
              <button onClick={onEdit} className="h-7 w-7 flex items-center justify-center rounded hover:bg-zinc-100 text-zinc-500" title="Edit sprint" data-testid={`edit-sprint-${sprint.sprint_id}`}>
                <PencilSimple size={12} />
              </button>
              <button onClick={onDelete} className="h-7 w-7 flex items-center justify-center rounded hover:bg-red-50 text-zinc-400 hover:text-red-600" title="Delete sprint" data-testid={`delete-sprint-${sprint.sprint_id}`}>
                <Trash size={12} />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Task row ────────────────────────────────────────────────
function SprintTaskRow({ task, members, sprints, onMove, onOpen, canEdit, storyPointsFieldId, dragHandleProps, isDragging, customStatuses }) {
  const assignee = members.find(m => m.user_id === task.assignee_id);
  const sp = storyPointsFieldId ? task.custom_fields?.[storyPointsFieldId] : null;
  const avatarStyle = assignee ? getAvatarStyle(assignee.user_id || assignee.name) : null;
  return (
    <div onClick={() => onOpen(task)}
      className={`flex items-center gap-3 px-4 py-2 cursor-pointer border-b border-zinc-100 group bg-white transition-shadow
        ${isDragging ? 'shadow-lg ring-1 ring-blue-300' : 'hover:bg-zinc-50'}`}
      data-testid={`backlog-task-${task.task_id}`}>
      {/* Drag handle (only when canEdit & dragHandleProps provided) */}
      {dragHandleProps && canEdit ? (
        <div {...dragHandleProps} onClick={(e) => e.stopPropagation()}
          className="flex-shrink-0 text-zinc-300 group-hover:text-zinc-500 cursor-grab active:cursor-grabbing select-none"
          title="Drag to move between sprint / backlog"
          data-testid={`drag-handle-${task.task_id}`}>
          <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor"><circle cx="2" cy="2" r="1.4"/><circle cx="2" cy="7" r="1.4"/><circle cx="2" cy="12" r="1.4"/><circle cx="8" cy="2" r="1.4"/><circle cx="8" cy="7" r="1.4"/><circle cx="8" cy="12" r="1.4"/></svg>
        </div>
      ) : <div className="w-[10px] flex-shrink-0" />}
      <WorkTypeIcon type={task.work_type || 'task'} size={14} />
      <span className={`text-[11px] font-mono w-20 truncate ${task.status === 'done' ? 'text-zinc-400 line-through' : 'text-zinc-400'}`}>{task.task_key}</span>
      <span className="flex-1 text-[13px] text-zinc-800 truncate">{task.title}</span>
      {sp != null && sp !== '' && (
        <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 text-[10px] font-bold" title={`Story Points: ${sp}`}>{sp}</span>
      )}
      {task.priority && <PriorityPill priority={task.priority} />}
      <Badge className={`text-[9px] border font-semibold ${statusTone(task.status)}`}>
        {statusLabel(task.status, customStatuses)}
      </Badge>
      {assignee ? (
        <Avatar className="h-6 w-6" title={assignee.name}><AvatarImage src={assignee.avatar} /><AvatarFallback className={`${avatarStyle.bg} text-white text-[9px] font-bold`}>{getInitials(assignee.name)}</AvatarFallback></Avatar>
      ) : <div className="h-6 w-6" />}
      {canEdit && (
        <Select value={task.sprint_id || 'backlog'} onValueChange={(v) => onMove(task.task_id, v === 'backlog' ? null : v)}>
          <SelectTrigger className="h-6 w-28 text-[10px] border-zinc-200 opacity-0 group-hover:opacity-100" onClick={(e) => e.stopPropagation()} data-testid={`move-task-${task.task_id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="backlog">→ Backlog</SelectItem>
            {sprints.filter(s => s.status !== 'completed').map(s => (
              <SelectItem key={s.sprint_id} value={s.sprint_id}>→ {s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

// ─── Burndown chart (SVG line chart) ────────────────────────
function BurndownChart({ data }) {
  if (!data || !data.days?.length) return (
    <div className="text-center py-10 text-[12px] text-zinc-400" data-testid="burndown-empty">
      Burndown will appear here once the sprint has a start + end date.
    </div>
  );
  const isHours = data.mode === 'hours';
  const total = isHours ? (data.total_hours_estimated || 0) : (data.total_story_points || 0);
  const unit = isHours ? 'h' : 'SP';
  const axisLabel = isHours ? 'Hours remaining' : 'SP remaining';
  const W = 640, H = 220, PAD_L = 40, PAD_R = 16, PAD_T = 18, PAD_B = 36;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const maxY = Math.max(total, 1);
  const n = data.days.length;
  const xOf = (i) => PAD_L + (n === 1 ? innerW / 2 : (i * innerW) / (n - 1));
  const yOf = (v) => PAD_T + innerH - (v / maxY) * innerH;
  const idealPts = data.days.map((d, i) => `${xOf(i)},${yOf(d.ideal_remaining)}`).join(' ');
  const actualPoints = data.days.filter(d => d.actual_remaining !== null);
  const actualPts = actualPoints.map((d) => `${xOf(data.days.indexOf(d))},${yOf(d.actual_remaining)}`).join(' ');
  const ticks = [0, 0.25, 0.5, 0.75, 1].map(t => Math.round(maxY * t * 10) / 10);
  return (
    <div className="p-6" data-testid="burndown-chart">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-56">
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={PAD_L} y1={yOf(t)} x2={W - PAD_R} y2={yOf(t)} stroke="#f4f4f5" strokeWidth="1" />
            <text x={PAD_L - 6} y={yOf(t) + 3} fontSize="9" fill="#a1a1aa" textAnchor="end">{t}</text>
          </g>
        ))}
        {[0, Math.floor(n / 2), n - 1].map(i => i < n && (
          <text key={i} x={xOf(i)} y={H - PAD_B + 14} fontSize="9" fill="#71717a" textAnchor="middle">
            {new Date(data.days[i].date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}
          </text>
        ))}
        <polyline points={idealPts} fill="none" stroke="#a1a1aa" strokeWidth="1.5" strokeDasharray="4 3" />
        {actualPoints.length > 0 && <polyline points={actualPts} fill="none" stroke="#4f46e5" strokeWidth="2" />}
        {actualPoints.map((d, i) => (
          <circle key={i} cx={xOf(data.days.indexOf(d))} cy={yOf(d.actual_remaining)} r="3" fill="#4f46e5">
            <title>{d.date}: {d.actual_remaining} {unit} remaining</title>
          </circle>
        ))}
        <text x={PAD_L - 28} y={PAD_T + innerH / 2} fontSize="9" fill="#71717a" transform={`rotate(-90, ${PAD_L - 28}, ${PAD_T + innerH / 2})`} textAnchor="middle">{axisLabel}</text>
      </svg>
      <div className="flex items-center justify-center gap-6 mt-2 text-[11px] text-zinc-500">
        <span className="flex items-center gap-1.5"><svg width="20" height="3"><line x1="0" y1="1.5" x2="20" y2="1.5" stroke="#a1a1aa" strokeDasharray="4 3" strokeWidth="1.5" /></svg> Ideal</span>
        <span className="flex items-center gap-1.5"><svg width="20" height="3"><line x1="0" y1="1.5" x2="20" y2="1.5" stroke="#4f46e5" strokeWidth="2" /></svg> Actual</span>
        <span className="ml-3">Total: <span className="font-bold text-zinc-900">{total} {unit}</span></span>
      </div>
    </div>
  );
}

// ─── Velocity chart ──────────────────────────────────────────
function VelocityChart({ velocity }) {
  if (!velocity?.sprints?.length) return (
    <div className="text-center py-10 text-[12px] text-zinc-400" data-testid="velocity-empty">
      Velocity chart will appear here after you complete your first sprint.
    </div>
  );
  const max = Math.max(...velocity.sprints.map(s => Math.max(s.planned_story_points, s.completed_story_points)), 1);
  return (
    <div className="p-6" data-testid="velocity-chart">
      <div className="flex items-end gap-4 h-52">
        {velocity.sprints.map(s => {
          const plannedH = (s.planned_story_points / max) * 100;
          const doneH = (s.completed_story_points / max) * 100;
          return (
            <div key={s.sprint_id} className="flex-1 flex flex-col items-center gap-1 min-w-0">
              <div className="flex items-end gap-1 h-40 w-full justify-center">
                <div className="w-5 bg-zinc-200 rounded-t relative group" style={{ height: `${plannedH}%` }} title={`Planned: ${s.planned_story_points} SP`}>
                  <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[9px] text-zinc-500 opacity-0 group-hover:opacity-100">{s.planned_story_points}</span>
                </div>
                <div className="w-5 bg-indigo-500 rounded-t relative group" style={{ height: `${doneH}%` }} title={`Completed: ${s.completed_story_points} SP`}>
                  <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[9px] text-indigo-700 font-semibold opacity-0 group-hover:opacity-100">{s.completed_story_points}</span>
                </div>
              </div>
              <span className="text-[10px] text-zinc-600 font-medium truncate max-w-full">{s.name}</span>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-center gap-6 mt-4 text-[11px] text-zinc-500">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-zinc-200 rounded" /> Planned</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-indigo-500 rounded" /> Completed</span>
        <span className="ml-4">Avg velocity: <span className="font-bold text-zinc-900" data-testid="avg-velocity">{velocity.avg_velocity} SP</span></span>
      </div>
    </div>
  );
}

// ─── Main Backlog View ──────────────────────────────────────
export default function BacklogView({ projectId, project, members, canEdit, canManageProject, onTaskClick, onOpenFullScreen, onTaskChanged, openCreateTask, refreshSignal }) {
  const confirm = useConfirm();
  const { getOrgHeaders, API } = useAuth();
  const { hasCap } = useSubscription() || {};
  const burndownUnlocked = hasCap?.('burndown') !== false;
  const h = useCallback(() => ({ withCredentials: true, headers: getOrgHeaders() }), [getOrgHeaders]);
  const [data, setData] = useState({ sprints: [], backlog: { task_count: 0 }, story_points_field_id: null });
  const [tasksBySprint, setTasksBySprint] = useState({}); // sprint_id or 'backlog' -> tasks[]
  const [velocity, setVelocity] = useState(null);
  const [burndown, setBurndown] = useState(null);
  // Burndown mode: 'story_points' or 'hours'. Persisted in localStorage so
  // the user's last choice sticks across reloads.
  const [burndownMode, setBurndownMode] = useState(() => {
    try { return localStorage.getItem('burndown_mode') || 'story_points'; } catch { return 'story_points'; }
  });
  const [collapsed, setCollapsed] = useState({});
  const [showCreateSprint, setShowCreateSprint] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(null); // sprint being completed
  const [editingSprint, setEditingSprint] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [{ data: sData }, { data: tasks }, { data: vel }] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/sprints`, h()),
        axios.get(`${API}/projects/${projectId}/tasks`, h()),
        axios.get(`${API}/projects/${projectId}/velocity`, h()),
      ]);
      setData(sData);
      setVelocity(vel);
      // Fetch burndown for the currently active sprint (if any) — skip on free plan.
      const active = (sData.sprints || []).find(s => s.status === 'active');
      if (active && burndownUnlocked) {
        try {
          const { data: bd } = await axios.get(`${API}/sprints/${active.sprint_id}/burndown?mode=${burndownMode}`, h());
          setBurndown(bd);
        } catch { setBurndown(null); }
      } else {
        setBurndown(null);
      }
      // group tasks by sprint_id (or 'backlog')
      const grouped = { backlog: [] };
      (sData.sprints || []).forEach(s => { grouped[s.sprint_id] = []; });
      tasks.forEach(t => {
        const k = t.sprint_id || 'backlog';
        if (!grouped[k]) grouped[k] = [];
        grouped[k].push(t);
      });
      setTasksBySprint(grouped);
    } catch (err) { console.error(err); }
  }, [projectId, API, h, burndownMode, burndownUnlocked]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  // Re-fetch when the parent signals a task-list change (e.g. CreateTaskDialog
  // just created a ticket via the "+" button). Without this hook the new
  // ticket would only show after a hard page refresh.
  useEffect(() => {
    if (refreshSignal === undefined) return;
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  const createSprint = async (payload) => {
    try {
      await axios.post(`${API}/projects/${projectId}/sprints`, payload, h());
      setShowCreateSprint(false);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create sprint'); }
  };

  const startSprint = async (sprintId) => {
    try { await axios.post(`${API}/sprints/${sprintId}/start`, {}, h()); fetchAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to start sprint'); }
  };

  const completeSprint = async (sprintId, moveToSprintId) => {
    try {
      const { data: res } = await axios.post(`${API}/sprints/${sprintId}/complete`, { move_to_sprint_id: moveToSprintId }, h());
      setShowCompleteDialog(null);
      fetchAll();
      toast.success(`Sprint completed: ${res.completed_story_points}/${res.planned_story_points} SP done${res.moved_task_count ? ` · ${res.moved_task_count} incomplete task(s) moved` : ''}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to complete sprint'); }
  };

  const deleteSprint = async (sprintId) => {
    if (!(await confirm({ title: 'Delete this sprint?', description: 'All tasks in this sprint will be moved back to the backlog. This cannot be undone.', confirmText: 'Delete sprint', variant: 'destructive' }))) return;
    try { await axios.delete(`${API}/sprints/${sprintId}`, h()); fetchAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete sprint'); }
  };

  const moveTask = async (taskId, toSprintId) => {
    try { await axios.patch(`${API}/tasks/${taskId}/sprint`, { sprint_id: toSprintId }, h()); fetchAll(); if (onTaskChanged) onTaskChanged(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to move task'); }
  };

  // DnD: react to drop events. Source droppableId is "sprint:<id>" or "backlog";
  // destination follows the same convention. We optimistically patch local
  // state so the row settles into the new section without flicker, then call
  // moveTask which re-fetches to reconcile.
  const handleDragEnd = (result) => {
    if (!result.destination || !canEdit) return;
    const srcKey = result.source.droppableId;
    const dstKey = result.destination.droppableId;
    if (srcKey === dstKey && result.source.index === result.destination.index) return;
    const taskId = result.draggableId;
    const targetSprintId = dstKey === 'backlog' ? null : (dstKey.startsWith('sprint:') ? dstKey.slice('sprint:'.length) : null);
    // Don't allow drops onto completed sprints.
    if (targetSprintId) {
      const targetSprint = data.sprints.find(s => s.sprint_id === targetSprintId);
      if (targetSprint?.status === 'completed') {
        toast.error('Cannot move tasks into a completed sprint');
        return;
      }
    }
    // Optimistic UI: remove from src group, push into dst group.
    setTasksBySprint(prev => {
      const srcArrKey = srcKey === 'backlog' ? 'backlog' : srcKey.slice('sprint:'.length);
      const dstArrKey = dstKey === 'backlog' ? 'backlog' : dstKey.slice('sprint:'.length);
      const next = { ...prev };
      const srcArr = [...(next[srcArrKey] || [])];
      const dstArr = srcArrKey === dstArrKey ? srcArr : [...(next[dstArrKey] || [])];
      const idx = srcArr.findIndex(t => t.task_id === taskId);
      if (idx === -1) return prev;
      const [moved] = srcArr.splice(idx, 1);
      const movedPatched = { ...moved, sprint_id: targetSprintId };
      dstArr.splice(result.destination.index, 0, movedPatched);
      next[srcArrKey] = srcArr;
      next[dstArrKey] = dstArr;
      return next;
    });
    moveTask(taskId, targetSprintId);
  };

  const updateSprint = async (sprintId, payload) => {
    try { await axios.put(`${API}/sprints/${sprintId}`, payload, h()); setEditingSprint(null); fetchAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to update sprint'); }
  };

  const spFieldId = data.story_points_field_id;

  return (
    <div className="flex-1 overflow-y-auto bg-white" data-testid="backlog-view">
      <div className="max-w-[1200px] mx-auto p-6 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Lightning size={18} weight="duotone" className="text-indigo-500" />
              <h2 className="font-['Outfit'] text-xl font-bold text-zinc-900">Backlog & Sprints</h2>
            </div>
            <p className="text-[12px] text-zinc-500 mt-0.5">Plan upcoming work, run sprints, and track velocity.</p>
          </div>
          {canManageProject && (
            <Button onClick={() => setShowCreateSprint(true)} size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="create-sprint-button">
              <Plus size={13} className="mr-1" /> Create Sprint
            </Button>
          )}
        </div>

        {/* Sprint sections */}
        <DragDropContext onDragEnd={handleDragEnd}>
        {data.sprints.map(s => (
          <div key={s.sprint_id} className="border border-zinc-200 rounded-lg overflow-hidden" data-testid={`sprint-section-${s.sprint_id}`}>
            <SprintHeader
              sprint={s}
              canEdit={canManageProject}
              collapsed={!!collapsed[s.sprint_id]}
              onToggle={() => setCollapsed(c => ({ ...c, [s.sprint_id]: !c[s.sprint_id] }))}
              onStart={() => startSprint(s.sprint_id)}
              onComplete={() => setShowCompleteDialog(s)}
              onEdit={() => setEditingSprint(s)}
              onDelete={() => deleteSprint(s.sprint_id)}
              onCreateTask={() => openCreateTask && openCreateTask({ sprint_id: s.sprint_id })}
            />
            {!collapsed[s.sprint_id] && (
              <Droppable droppableId={`sprint:${s.sprint_id}`} isDropDisabled={s.status === 'completed' || !canEdit}>
                {(provided, snapshot) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}
                    className={`divide-y divide-zinc-100 transition-colors ${snapshot.isDraggingOver ? 'bg-blue-50/40' : ''}`}
                    data-testid={`sprint-tasks-${s.sprint_id}`}>
                    {(tasksBySprint[s.sprint_id] || []).length === 0 ? (
                      <div className="px-4 py-6 text-center text-[11px] text-zinc-400">
                        {snapshot.isDraggingOver ? 'Drop here to add to this sprint' : 'No tasks yet. Add one or drag from backlog.'}
                      </div>
                    ) : (
                      (tasksBySprint[s.sprint_id] || []).map((t, idx) => (
                        <Draggable key={t.task_id} draggableId={t.task_id} index={idx} isDragDisabled={!canEdit || s.status === 'completed'}>
                          {(p, sn) => (
                            <div ref={p.innerRef} {...p.draggableProps} style={p.draggableProps.style}>
                              <SprintTaskRow
                                task={t} members={members} sprints={data.sprints}
                                onMove={moveTask} onOpen={onTaskClick}
                                canEdit={canEdit && s.status !== 'completed'}
                                storyPointsFieldId={spFieldId}
                                customStatuses={project?.custom_statuses}
                                dragHandleProps={p.dragHandleProps}
                                isDragging={sn.isDragging}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))
                    )}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            )}
          </div>
        ))}

        {/* Backlog section */}
        <div className="border border-zinc-200 rounded-lg overflow-hidden" data-testid="backlog-section">
          <div className="flex items-center gap-3 px-4 py-3 bg-zinc-50 border-b border-zinc-200">
            <button onClick={() => setCollapsed(c => ({ ...c, backlog: !c.backlog }))} className="text-zinc-500 hover:text-zinc-900 transition-colors flex-shrink-0">
              {collapsed.backlog ? <CaretRight size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
            </button>
            <Target size={14} className="text-zinc-500" />
            <h3 className="font-['Outfit'] text-[14px] font-bold text-zinc-900 flex-1">Backlog</h3>
            <span className="text-[11px] text-zinc-500">{data.backlog?.task_count || 0} tasks · {data.backlog?.story_points_total || 0} SP</span>
            {canEdit && (
              <button onClick={() => openCreateTask && openCreateTask({ sprint_id: null })} className="h-7 w-7 flex items-center justify-center rounded hover:bg-zinc-100 text-zinc-500" title="Add task to backlog" data-testid="add-task-to-backlog">
                <Plus size={13} />
              </button>
            )}
          </div>
          {!collapsed.backlog && (
            <Droppable droppableId="backlog" isDropDisabled={!canEdit}>
              {(provided, snapshot) => (
                <div ref={provided.innerRef} {...provided.droppableProps}
                  className={`divide-y divide-zinc-100 transition-colors ${snapshot.isDraggingOver ? 'bg-blue-50/40' : ''}`}>
                  {(tasksBySprint.backlog || []).length === 0 ? (
                    <div className="px-4 py-8 text-center text-[11px] text-zinc-400">
                      {snapshot.isDraggingOver ? 'Drop here to send to Backlog' : 'Backlog is empty. Create a task to get started.'}
                    </div>
                  ) : (
                    (tasksBySprint.backlog || []).map((t, idx) => (
                      <Draggable key={t.task_id} draggableId={t.task_id} index={idx} isDragDisabled={!canEdit}>
                        {(p, sn) => (
                          <div ref={p.innerRef} {...p.draggableProps} style={p.draggableProps.style}>
                            <SprintTaskRow
                              task={t} members={members} sprints={data.sprints}
                              onMove={moveTask} onOpen={onTaskClick} canEdit={canEdit}
                              storyPointsFieldId={spFieldId}
                              customStatuses={project?.custom_statuses}
                              dragHandleProps={p.dragHandleProps}
                              isDragging={sn.isDragging}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          )}
        </div>
        </DragDropContext>

        {/* Burndown (active sprint only) */}
        {!burndownUnlocked ? (
          <UpgradeLockCard
            title="Burndown charts are a Business plan feature"
            description="Upgrade to see real-time burndown for active sprints in both story points and hours."
            featureName="burndown"
            testId="burndown-upgrade-lock"
          />
        ) : burndown && (
          <div className="border border-zinc-200 rounded-lg overflow-hidden" data-testid="burndown-section">
            <div className="flex items-center gap-2 px-4 py-3 bg-zinc-50 border-b border-zinc-200">
              <ChartLine size={14} className="text-indigo-500" weight="duotone" />
              <h3 className="font-['Outfit'] text-[14px] font-bold text-zinc-900">Burndown — {burndown.sprint_name}</h3>
              {/* Mode toggle: only show Hours option when the project has time tracking turned on. */}
              {project?.hours_per_story_point ? (
                <div className="ml-3 inline-flex rounded-md border border-zinc-200 bg-white overflow-hidden" data-testid="burndown-mode-toggle">
                  {[
                    { value: 'story_points', label: 'SP' },
                    { value: 'hours',        label: 'Hours' },
                  ].map(opt => (
                    <button key={opt.value} type="button"
                      data-testid={`burndown-mode-${opt.value}`}
                      onClick={() => { setBurndownMode(opt.value); try { localStorage.setItem('burndown_mode', opt.value); } catch {} }}
                      className={`px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${burndownMode === opt.value ? 'bg-indigo-600 text-white' : 'text-zinc-500 hover:bg-zinc-50'}`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              ) : null}
              <span className="text-[11px] text-zinc-500 ml-auto">{burndown.start_date} → {burndown.end_date}</span>
            </div>
            <BurndownChart data={burndown} />
          </div>
        )}

        {/* Velocity */}
        <div className="border border-zinc-200 rounded-lg overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-3 bg-zinc-50 border-b border-zinc-200">
            <ChartLine size={14} className="text-zinc-500" weight="duotone" />
            <h3 className="font-['Outfit'] text-[14px] font-bold text-zinc-900">Velocity (last {velocity?.sprints?.length || 0} completed sprints)</h3>
          </div>
          <VelocityChart velocity={velocity} />
        </div>
      </div>

      {/* Create Sprint Dialog */}
      <SprintFormDialog
        open={showCreateSprint}
        onClose={() => setShowCreateSprint(false)}
        onSubmit={createSprint}
        title="Create sprint"
      />

      {/* Edit Sprint Dialog */}
      <SprintFormDialog
        open={!!editingSprint}
        initial={editingSprint}
        onClose={() => setEditingSprint(null)}
        onSubmit={(payload) => updateSprint(editingSprint.sprint_id, payload)}
        title="Edit sprint"
      />

      {/* Complete Sprint Dialog */}
      {showCompleteDialog && (
        <CompleteSprintDialog
          sprint={showCompleteDialog}
          otherSprints={data.sprints.filter(s => s.sprint_id !== showCompleteDialog.sprint_id && s.status !== 'completed')}
          incompleteCount={(tasksBySprint[showCompleteDialog.sprint_id] || []).filter(t => t.status !== 'done').length}
          onClose={() => setShowCompleteDialog(null)}
          onSubmit={(moveToId) => completeSprint(showCompleteDialog.sprint_id, moveToId)}
        />
      )}
    </div>
  );
}

// ─── Sprint form dialog (create / edit) ─────────────────────
function SprintFormDialog({ open, onClose, onSubmit, initial, title }) {
  const [name, setName] = useState('');
  const [goal, setGoal] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  useEffect(() => {
    if (open) {
      setName(initial?.name || '');
      setGoal(initial?.goal || '');
      setStartDate(initial?.start_date ? initial.start_date.slice(0, 10) : '');
      setEndDate(initial?.end_date ? initial.end_date.slice(0, 10) : '');
    }
  }, [open, initial]);
  const today = new Date().toISOString().slice(0, 10);
  // An ACTIVE or COMPLETED sprint's start date is historical — it already
  // happened, so the start-date input is read-only. Only PLANNED sprints
  // (and new sprints) accept editable start dates. End dates can always
  // move forward, but never into the past — regardless of sprint status.
  const sprintStatus = initial?.status; // 'planned' | 'active' | 'completed' | undefined (new)
  const startIsLocked = sprintStatus === 'active' || sprintStatus === 'completed';
  // For start-date min: locked → no min needed (it's disabled); else today.
  const startMin = startIsLocked ? undefined : today;
  // For end-date min: the later of (today, current startDate). Never accept
  // an end-date in the past — including when editing an active sprint.
  const endMin = startDate && startDate > today ? startDate : today;
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    // Date validation (mirrored from the input `min` attribute so users
    // can't bypass it by typing the date directly):
    //   • Start date — must be >= today UNLESS sprint is active/completed
    //     (then the input is disabled but we still allow the existing value
    //     to round-trip).
    //   • End date — must be >= today AND >= start date.
    if (startDate && !startIsLocked && startDate < today) {
      toast.error('Start date cannot be in the past');
      return;
    }
    if (endDate) {
      if (endDate < today) {
        toast.error('End date cannot be in the past');
        return;
      }
      if (startDate && endDate < startDate) {
        toast.error('End date must be on or after start date');
        return;
      }
    }
    if (endDate && !startDate) {
      toast.error('Please set a start date first');
      return;
    }
    onSubmit({ name: name.trim(), goal: goal.trim(), start_date: startDate || null, end_date: endDate || null });
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="sprint-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-lg font-bold">{title}</DialogTitle>
          <DialogDescription className="text-[12px] text-zinc-500">A sprint is a time-boxed iteration where the team commits to a set of tasks.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 mt-2">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-zinc-400 block mb-1">Name</label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Sprint 1" required autoFocus data-testid="sprint-name-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-zinc-400 block mb-1">Goal (optional)</label>
            <Textarea value={goal} onChange={e => setGoal(e.target.value)} placeholder="What does this sprint aim to deliver?" rows={2} className="resize-none" data-testid="sprint-goal-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-zinc-400 block mb-1">
                Start date{startIsLocked && <span className="ml-1 text-zinc-400 normal-case font-normal lowercase tracking-normal">· locked (sprint {sprintStatus})</span>}
              </label>
              <Input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                min={startMin}
                disabled={startIsLocked}
                data-testid="sprint-start-date"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-zinc-400 block mb-1">End date</label>
              <Input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                min={endMin}
                data-testid="sprint-end-date"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="sprint-submit">{initial ? 'Save' : 'Create sprint'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Complete sprint dialog ─────────────────────────────────
function CompleteSprintDialog({ sprint, otherSprints, incompleteCount, onClose, onSubmit }) {
  const [moveTo, setMoveTo] = useState('backlog');
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="complete-sprint-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-lg font-bold">Complete "{sprint.name}"?</DialogTitle>
          <DialogDescription className="text-[12px] text-zinc-500">
            This sprint will be marked as completed. Velocity will be recorded.
          </DialogDescription>
        </DialogHeader>
        {incompleteCount > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2 text-[12px] text-amber-900" data-testid="incomplete-warning">
            <p className="font-semibold mb-2">{incompleteCount} task{incompleteCount > 1 ? 's' : ''} still incomplete. Where should they go?</p>
            <Select value={moveTo} onValueChange={setMoveTo}>
              <SelectTrigger className="bg-white h-9" data-testid="move-incomplete-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="backlog">Move to Backlog</SelectItem>
                {otherSprints.map(s => (
                  <SelectItem key={s.sprint_id} value={s.sprint_id}>Move to "{s.name}"</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <DialogFooter className="mt-3">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSubmit(moveTo === 'backlog' ? null : moveTo)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="confirm-complete-sprint">
            <CheckCircle size={13} className="mr-1" weight="fill" /> Complete Sprint
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
